import React, { useState, useMemo } from 'react';
import DataTable from 'react-data-table-component';
import Buttons from '../../components/Button';
import { UserInput, Vendor } from '../../interfaces/user';
import SearchInput from '../../components/SearchInput';
import { deleteVendor } from '../../graphql/api/userApi';
import NoData from '../../components/NoData';

interface VendorManagementProps {
  users: UserInput[];
  vendors: Vendor[] | Vendor[][];
  setVendors: (vendors: Vendor[]) => void;
  setActiveTab: (tab: 'user' | 'vendor' | 'shop') => void;
  setIsEditing: (editing: boolean) => void;
  setVendorForm: (form: any) => void;
  setSelections: (selections: any) => void;
}

const VendorManagement: React.FC<VendorManagementProps> = ({
  users,
  vendors,
  setVendors,
  setActiveTab,
  setIsEditing,
  setVendorForm,
  setSelections
}) => {
  const [searchText, setSearchText] = useState('');

  // Ensure vendors is always a flat array
  const flatVendors: Vendor[] = useMemo(() => vendors.flat?.() ?? [], [vendors]);

  const filteredVendors = useMemo(() => {
    const searchTerm = searchText.toLowerCase();
    return flatVendors.filter(vendor =>
      (vendor.businessName?.toLowerCase() ?? '').includes(searchTerm) ||
      (vendor.address?.toLowerCase() ?? '').includes(searchTerm) ||
      (users.find(u => u.id === vendor.userId)?.username?.toLowerCase() ?? '').includes(searchTerm)
    );
  }, [flatVendors, users, searchText]);

  const handleVendorEdit = (vendor: Vendor) => {
    // Always clear previous data
    setVendorForm({
      id: '',
      businessName: '',
      address: '',
      email: '',
      phone: '',
      panno: '',
      gstno: ''
    });

    // Then set current vendor's values
    setVendorForm({
      id: vendor.id ?? '',
      businessName: vendor.businessName ?? '',
      address: vendor.address ?? '',
      email: vendor.email ?? '',
      phone: vendor.phone ?? '',
      panno: vendor.panno ?? '',
      gstno: vendor.gstno ?? ''
    });

    setSelections({
      userId: vendor.userId?.toString() ?? '',
      vendorId: '',
      editingId: vendor.id?.toString() ?? ''
    });

    setIsEditing(true);
    setActiveTab('vendor');
  };



  const vendorColumns = [
    {
      name: 'Vendor Name',
      selector: (row: Vendor) => row.businessName,
      sortable: true,
    },
    {
      name: 'Address',
      selector: (row: Vendor) => row.address,
      sortable: true,
    },
    {
      name: 'Associated User',
      cell: (row: Vendor) => {
        const user = users.find(u => u.id === row.userId);
        return user?.username || 'Unknown';
      },
      sortable: true,
    },
    {
      name: 'Actions',
      cell: (row: Vendor) => (
        <div className="action-buttons">
          <Buttons
            text="Edit"
            onClick={() => handleVendorEdit(row)}
            variant="edit"
          />
          <Buttons
            text="Delete"
            onClick={() => deleteVendor(row.id)}
            variant="delete"
          />
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];
  return (
    <div>
      <div className="mb-6">
        <SearchInput searchText={searchText} setSearchText={setSearchText} />
      </div>
      <DataTable
        data={filteredVendors}
        columns={vendorColumns}
        pagination
        highlightOnHover
        striped
        responsive
        paginationPerPage={5}
        noDataComponent={<NoData message='No vendors found' />}
        customStyles={{
          headCells: {
            style: {
              fontWeight: 'bold',
              backgroundColor: '#f8f9fa',
            },
          },
          cells: {
            style: {
              padding: '12px',
            },
          },
        }}
      />
    </div>
  );
};

export default VendorManagement;
